import Autolinker from "autolinker";
import { floor, functions, includes, isEqual, isObject, memoize, MemoizedFunction, merge, omit, random, reduce, toNumber } from "lodash";
import { PropsWithChildren } from "react";
import xss from "xss";
import { replaceToEmoji } from "./emoji";
import { EVENT_BOT_LINK_BUTTON, EVENT_INTERNAL_DISCOVERY, EVENT_INTERNAL_LINK, EVENT_INVITE_LINK, EVENT_NEW_SNACKBAR, EVENT_PROFILE_LINK, EVENT_ROOM_MENTION } from "../constants/events";
import {
  REGEX_A2HS_MOBILE,
  REGEX_A2HS_TABLET,
  REGEX_BOLD_CHARACTER,
  REGEX_BOT_LINK,
  REGEX_HALF_SPACE,
  REGEX_INTERNAL_DISCOVERY,
  REGEX_IS_INTERNAL_LINK,
  REGEX_IS_INVITE_LINK,
  REGEX_IS_INVITE_PROFILE_LINK,
  REGEX_WORD_CHARACER,
} from "../constants/regex";
import { OptionsObject } from "notistack";
import { Discovery, DiscoveryField, Room, RoomMessage, RoomMessageType } from "../proto/Global_pb";
import { memoryHistory } from "../index";
import { POPUP_ADD_TO_HS, ROUTE_LOGIN } from "../constants/routes";
import { countries } from "../constants/globals";
import { restHandler } from "./rest";
import { STICKER_USER_LIST } from "../constants/webservices";
import { AxiosResponse } from "axios";
import { StickerGroupType } from "../containers/popup/popup-sticker-group";
import store from "../store";
import { addStickerGroup } from "../store/messenger/actions";
import { getStorage } from "./storage";
import { STORAGE_A2HS } from "../constants/localstorage";
import { goToPopup } from "./services";
import { ClientGetDiscovery, ClientGetDiscoveryResponse } from "../proto/ClientGetDiscovery_pb";
import { protoHandler } from "./proto";
import { CLIENT_GET_DISCOVERY } from "../constants/methods";
import { MESSAGE_INPUT_ID } from "../constants/selectors";

export const isDevMod = !process.env.NODE_ENV || process.env.NODE_ENV === "development";

export const msSleep = (ms: number): Promise<string> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("");
    }, ms);
  });
};

export const stringToUint8Array = (string: string): Uint8Array => {
  return new Uint8Array(
    // @ts-ignore
    [].map.call(string, (x: string) => x.charCodeAt(0))
  );
};

export const arrayBufferToString = (buffer: ArrayBuffer): string => {
  // @ts-ignore
  return String.fromCharCode.apply(null, new Uint8Array(buffer));
};

export const randomString = (length: number, chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXTZabcdefghiklmnopqrstuvwxyz") => {
  let str = "";
  const charsLen = chars.length;

  for (let i = 0; i < length; i++) {
    str += chars.charAt(floor(Math.random() * charsLen));
  }
  return str;
};

export const areEqual = (value: PropsWithChildren<any>, other: PropsWithChildren<any>): boolean => {
  isObject(value) && (value = omit(value, functions(value)));
  isObject(other) && (other = omit(other, functions(other)));
  return isEqual(value, other);
};

export const memorize: { <T extends (...args: any) => any>(func: T): T & MemoizedFunction } = (callback) => {
  return memoize(callback, (...params) => JSON.stringify(params));
};

export const flattenKeys = (obj: object, path: string[] = []): object => {
  return !isObject(obj) ? { [path.join(".")]: obj } : reduce(obj, (cum, next, key) => merge(cum, flattenKeys(next, [...path, key])), {});
};

const boldIt = (str: string): string => `<b>${str.substring(str.indexOf("**") + 2, str.lastIndexOf("**"))}</b>`;

const removeExtraHalfSpaces = (str: string): string => (!str.trim().match(REGEX_WORD_CHARACER) ? str.replace(REGEX_HALF_SPACE, "") : str);

function parseBotLink(string: string): string {
  const buttonText = string.match(REGEX_BOT_LINK) as Array<string>;
  buttonText.forEach((textWithSlash) => {
    string = string.replace(textWithSlash, `<a href="#" onclick=" document.dispatchEvent(new CustomEvent('${EVENT_BOT_LINK_BUTTON}' , { detail : '${textWithSlash}' }))">${textWithSlash}</a>`);
  });
  return string;
}

function parseWebLinks(string: string): string {
  string = string.startsWith("http") ? string.replaceAll(/[\u200B-\u200D\uFEFF]/g, "") : string;

  string = Autolinker.link(string, {
    newWindow: false,
    urls: true,
    email: true,
    phone: true,
    mention: "twitter",
    hashtag: "twitter",
    stripPrefix: false,
    stripTrailingSlash: false,
    truncate: { length: 0, location: "end" },
    replaceFn: (match: any) => {
      let tag = match.buildTag();
      let type = match.getType();
      switch (type) {
        case "hashtag":
        case "mention": {
          tag.setAttrs({ href: `#` });
          tag.setAttr("onclick", `document.dispatchEvent(new CustomEvent('${EVENT_ROOM_MENTION}' , { detail : '${match.getMatchedText()}' }))`);
          break;
        }
        case "url": {
          if (tag.getInnerHTML().match(REGEX_IS_INTERNAL_LINK)) {
            tag.setAttrs({ href: `#` });
            if (tag.getInnerHTML().match(/discovery/g)) {
              tag.setAttr("onclick", `event.stopPropagation(); document.dispatchEvent(new CustomEvent('${EVENT_INTERNAL_DISCOVERY}' , { detail : '${match.getMatchedText()}' }))`);
            } else if (tag.getInnerHTML().match(/profile/g)) {
              tag.setAttr("onclick", `event.stopPropagation(); document.dispatchEvent(new CustomEvent('${EVENT_PROFILE_LINK}' , { detail : '${match.getMatchedText()}' }))`);
            } else {
              // tag.setAttr("onclick", `event.stopPropagation(); window.open('${match.getMatchedText()}' , '_blank')`);
              tag.setAttr("onclick", `event.stopPropagation(); document.dispatchEvent(new CustomEvent('${EVENT_INVITE_LINK}' , { detail : '${match.getMatchedText()}' }))`);
            }
          } else if (tag.getInnerHTML().match(REGEX_IS_INVITE_LINK) || tag.getInnerHTML().match(REGEX_IS_INVITE_PROFILE_LINK)) {
            tag.setAttrs({ href: `#` });
            if (tag.getInnerHTML().match(/join/g)) {
              tag.setAttr("onclick", `event.stopPropagation(); document.dispatchEvent(new CustomEvent('${EVENT_INVITE_LINK}' , { detail : '${match.getMatchedText()}' }))`);
            }
          } else {
            tag.setAttrs({ target: "_blank" });
            tag.setAttr("onclick", "event.stopPropagation();");
          }
          break;
        }
        case "email": {
          tag.setAttrs({ target: "_blank" });
          tag.setAttr("onclick", `event.stopPropagation(); window.open('mailto:${match.getMatchedText()}')`);
          break;
        }
      }
      return tag;
    },
  });
  return string;
}

function parseMarkdown(string: string): string {
  // ** BOLD **
  string = string.replace(REGEX_BOLD_CHARACTER, (match) => boldIt(match));

  return string;
}

type parseMessageOption = {
  parseLinks: boolean;
  parseEmoji: boolean;
  isReplace: boolean;
  lineType: "html" | "plain";
  isBot: boolean;
};
export const parseMessage = memorize((string?: string, options: Partial<parseMessageOption> = {}): string => {
  const { isReplace = true, parseEmoji = true, parseLinks = true, isBot = false, lineType = "plain" } = options;

  if (string === undefined) return "";

  // Handle new line characters if send_by_enter_key is false
  string = string.replace(/<div[^<]*?>/g, "").replace(/<\/div[^<]*?>/g, "<br>");
  // Remove all span tags. When user changes direction of input, a span tag will add to the input
  string = string.replace(/<span[^<]*?>/g, "").replace(/<\/span[^<]*?>/g, "");

  string = xss(string, {
    css: false,
    allowCommentTag: false,
    whiteList: {},
    onIgnoreTag: (tag: string) => {
      if (lineType === "plain" && ["br", "hr"].includes(tag)) {
        return "\n";
      }
    },
  });

  string = lineType === "plain" ? string.replaceAll("<br>", "") : string;

  string = parseEmoji ? replaceToEmoji(string) : string;

  if (isBot && string.match(REGEX_BOT_LINK) && !string.match(REGEX_INTERNAL_DISCOVERY)) string = parseBotLink(string);

  string = parseLinks ? parseWebLinks(string) : string;

  string = isReplace ? string.replaceAll("&nbsp;", " ") : parseMarkdown(string);

  string = removeExtraHalfSpaces(string);

  return string;
});

export const parseEnter = (text: string): string => {
  return text.replace(/(?:\r\n|\r|\n)/g, "<br>");
};

export const toast = (message: any, options?: OptionsObject) => {
  document.dispatchEvent(
    new CustomEvent(EVENT_NEW_SNACKBAR, {
      detail: {
        message,
        options,
      },
    })
  );
};

export const encodeMessageChar = (message: string): string => {
  const convertedMessage = new DOMParser().parseFromString(message, "text/html").documentElement.textContent;
  return convertedMessage ? convertedMessage : message;
};

export const milliSecondsToTimer = memorize((milliseconds?: number) => {
  if (!milliseconds || milliseconds === -1) return "00:00";

  const minutes = Math.floor(milliseconds / 60),
    seconds = Math.floor(milliseconds % 60);

  const finalTime = `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;

  if (!finalTime || finalTime === " ") return "00:00";
  return finalTime;
});

export const messageTypeHelper = (messageType?: RoomMessage.AsObject["messageType"]) => {
  return {
    isVideo: () => includes([RoomMessageType.VIDEO, RoomMessageType.VIDEO_TEXT], messageType),
    isImage: () => includes([RoomMessageType.IMAGE, RoomMessageType.IMAGE_TEXT], messageType),
    isAudio: () => includes([RoomMessageType.AUDIO, RoomMessageType.AUDIO_TEXT], messageType),
    isVoice: () => includes([RoomMessageType.VOICE], messageType),
    isFile: () => includes([RoomMessageType.FILE_TEXT, RoomMessageType.FILE], messageType),
    isGif: () => includes([RoomMessageType.GIF, RoomMessageType.GIF_TEXT], messageType),
    isCaption: () => includes([RoomMessageType.TEXT], messageType),
    isStory: () => includes([RoomMessageType.STORY_REPLY], messageType),
    isHaveText: () =>
      includes([RoomMessageType.AUDIO_TEXT, RoomMessageType.GIF_TEXT, RoomMessageType.IMAGE_TEXT, RoomMessageType.FILE_TEXT, RoomMessageType.VIDEO_TEXT, RoomMessageType.TEXT], messageType),
  };
};

export const callbackToPromise = <T>(callback: any): Promise<T> => {
  return new Promise((resolve) => {
    callback((response: any) => resolve(response));
  });
};

export const randomIntFromInterval = (min: number, max: number): number => random(min, max);

export const generateRandomId = ([min, max]: [number, number] = [555555555555555555, 999999999999999999]): string => randomIntFromInterval(min, max).toString();

export function windowOpen(url: string, specs: "_blank" | "_self" | "_parent") {
  if (!url.match(/^https?:\/\//i)) {
    url = "http://" + url;
  }
  return window.open(url, specs);
}

interface copyReadOnlyOpt<T> {
  modify?: Partial<T>;
}

export function copyReadOnlyObj<T>(obj: T, options?: copyReadOnlyOpt<T>): T {
  const { modify = {} } = options ?? {};

  const copiedObj: any = {};
  for (const key in obj) {
    copiedObj[key] = obj[key];
  }

  const modifiedObj = { ...copiedObj, ...modify };

  return modifiedObj;
}

export const checkHashbang = () => {
  const urlIncludeHashbang = window.location.hash.search("#!/") >= 0;
  const phoneNumber = urlIncludeHashbang && window.location.href.indexOf("phone=") === -1 ? "" : window.location.href.slice(window.location.href.indexOf("phone=") + "phone=".length);
  if (!urlIncludeHashbang || !phoneNumber) return;

  const safePhoneNumber = phoneNumber.startsWith("0") || phoneNumber.startsWith("98") ? phoneNumber?.replace(/^0|98+/, "") : phoneNumber;
  if (phoneNumber) memoryHistory.push(ROUTE_LOGIN, { phoneNumber: safePhoneNumber });
};

export const getRoomUsername = (room: Room.AsObject) => {
  const username = room?.chatRoomExtra?.peer?.username || room?.channelRoomExtra?.publicExtra?.username || room?.groupRoomExtra?.publicExtra?.username;
  return username ? `@${username}` : room.id;
};

export const getCountryName = (countryCode: string): string =>
  Object.keys(countries)
    .find((c) => countries[c] === countryCode)
    ?.toUpperCase() || "";

export const resend = <T>(promiseFunction: Promise<T>, retry = 3) => {
  return new Promise<T>((resolve, reject) => {
    promiseFunction
      .then((result) => {
        resolve(result);
      })
      .catch((err) => {
        const shouldResend = retry !== 0;
        if (!shouldResend) return reject(err);
        return (
          msSleep(1000)
            .then(resend.bind(null, promiseFunction, retry - 1))
            //  @ts-ignore
            .then((res: any) => resolve(res))
            .catch(reject)
        );
      });
  });
};

export const focusOnInput = (cb?: (...args: any[]) => void) => {
  const editor = document.getElementById(MESSAGE_INPUT_ID)!;
  const selection = window.getSelection();
  const range = document.createRange();

  if (!selection || !range) {
    return;
  }

  setTimeout(() => {
    editor.focus();

    range.selectNodeContents(editor);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);

    cb?.();
  });
};

export const getStickerGroups = memoize(() => {
  restHandler({ url: STICKER_USER_LIST }, true).then((res: AxiosResponse<{ data: StickerGroupType[] }>) => {
    store.dispatch(addStickerGroup(res.data.data));
  });
});

export const checkA2HS = () => {
  const ua = navigator.userAgent;
  const isMobile = REGEX_A2HS_TABLET.test(ua) || REGEX_A2HS_MOBILE.test(ua);
  if (isMobile && !getStorage(STORAGE_A2HS)) {
    setTimeout(() => {
      goToPopup(POPUP_ADD_TO_HS);
    }, 3000);
  }
};

export const isNumeric = (str: string | any) => {
  if (!str || typeof str != "string") return false;
  return !isNaN(parseInt(str)) && !isNaN(parseFloat(str));
};

export const getDiscovery = memoize((options: Partial<ClientGetDiscovery.AsObject>) => protoHandler(CLIENT_GET_DISCOVERY, options));

export const getPageIdFromDiscovery = (discovery: Array<DiscoveryField.AsObject>, itemId: number): string | undefined => {
  if (discovery.length === 0) return undefined;
  const item = discovery[0];
  if (!item || item.id !== itemId) {
    discovery.shift();
    return getPageIdFromDiscovery(discovery, itemId);
  }
  return item.value;
};

export const getPageIdFromDiscoveriesList = (fieldList: ClientGetDiscoveryResponse.AsObject["discoveriesList"], itemId: number): string | undefined => {
  if (fieldList.length === 0) return undefined;

  const currentItem = fieldList[0];
  const foundedItem = getPageIdFromDiscovery(currentItem.discoveryfieldsList, itemId);

  if (foundedItem) return foundedItem; /* value is pageId */
  fieldList.shift();
  return getPageIdFromDiscoveriesList(fieldList, itemId) as string;
};
